import { useState } from 'react';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { suggestEmailFix } from '../lib/emailTypo';
import { Button } from './ui/button';
import { Input } from './ui/input';

// 2026-09-17 — "Change email" for password accounts (ProfilePage). Also the
// landing spot for the Stage "We couldn't reach your email → Fix email" card.
// Mobile mirror: SecurityScreen "Email" card.
export const ChangeEmailCard = ({ autoOpen = false }) => {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(autoOpen);
  const [next, setNext] = useState('');
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const suggestion = suggestEmailFix(next);

  if (!user?.has_password || user?.is_guest) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!next.includes('@')) return toast.error('Enter a valid email address.');
    setBusy(true);
    try {
      const { data } = await authAPI.changeEmail(next.trim(), pwd);
      toast.success(data?.message || 'Email updated.');
      setNext(''); setPwd(''); setOpen(false);
      refreshUser();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not update email. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4" data-testid="change-email-card">
      <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
        <Mail className="h-4 w-4" /> Email
      </h3>
      <p className="text-xs text-muted-foreground mb-3 break-all" data-testid="change-email-current">{user.email}</p>
      {!open ? (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="change-email-open-btn">
          Change email
        </Button>
      ) : (
        <form onSubmit={submit} className="space-y-2">
          <Input
            type="email"
            placeholder="New email"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="email"
            data-testid="change-email-new"
          />
          {suggestion && (
            <button
              type="button"
              className="text-left text-xs text-[hsl(var(--accent))] hover:underline underline-offset-2"
              onClick={() => setNext(suggestion)}
              data-testid="change-email-suggestion"
            >
              Did you mean <span className="font-semibold">{suggestion}</span>?
            </button>
          )}
          <Input
            type="password"
            placeholder="Current password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            autoComplete="current-password"
            data-testid="change-email-password"
          />
          <p className="text-[10px] text-muted-foreground">
            We&apos;ll send a confirmation to both your old and new address. You stay signed in.
          </p>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy || !next || !pwd} data-testid="change-email-submit">
              {busy ? 'Updating…' : 'Update email'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={busy} data-testid="change-email-cancel">
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ChangeEmailCard;
